import { translate, translateRich, useLocale } from "../../localization";
import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AppButton, AppTextField } from "../ui";
import {
  elementLabelDiffersFromOriginal,
  flatLabelDiffersFromOriginal,
  getElementLabelStats,
  getFlatLabelStats,
  resetAllCurrentElementLabels,
  resetAllFlatLabels,
  resetElementLabel,
  resetElementLabelField,
  resetFlatLabel,
  resetFlatLabelField,
  updateElementLabelField,
  updateFlatLabelField,
} from "../../utility/guideElementLabels";
import "./ElementLabelsDialog.scss";

const readableOriginal = (value) => (value ? value : translate("Empty"));

const LabelField = ({
  label,
  value,
  originalValue,
  multiline = false,
  onChange,
  onReset,
  required = false,
}) => {
  useLocale();
  const invalid = required && !String(value || "").trim();
  const differs = value !== originalValue;

  return (
    <Box className="element-labels-dialog__field">
      <Box className="element-labels-dialog__field-heading">
        <Typography variant="caption" className="element-labels-dialog__original-value">{translateRich("Current System: {value0}", {
  value0: readableOriginal(originalValue)
})}</Typography>
        <AppButton
          kind="ghost"
          onClick={onReset}
          disabled={!differs}
          className="element-labels-dialog__reset-field"
        >{translate("Reset")}</AppButton>
      </Box>
      <AppTextField
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        error={invalid}
        helperText={invalid ? translate("{value0} is required.", {
  value0: label
}) : undefined}
        fullWidth
      />
    </Box>
  );
};

const FlatLabelsSection = ({
  title,
  typeName,
  labels,
  originalLabels,
  idField,
  fields,
  searchText,
  filter,
  onChange,
}) => {
  const locale = useLocale();
  const guideById = useMemo(
    () => new Map(labels.map((label) => [label[idField], label])),
    [idField, labels, locale]
  );
  const visibleLabels = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase();
    return originalLabels.filter((original) => {
      const guideLabel = guideById.get(original[idField]);
      const differs = flatLabelDiffersFromOriginal(guideLabel, original, fields);
      if (filter === "different" && !differs) return false;
      if (!query) return true;
      return fields.some((field) =>
        [original[field], guideLabel?.[field]].some((value) =>
          String(value || "").toLocaleLowerCase().includes(query)
        )
      );
    });
  }, [fields, filter, guideById, idField, originalLabels, searchText, locale]);

  if (originalLabels.length === 0) return null;

  return (
    <Box className="element-labels-dialog__sub-elements">
      <Typography variant="h6">{title}</Typography>
      <Box className="app-row-list element-labels-dialog__list">
        {visibleLabels.map((original) => {
          const id = original[idField];
          const guideLabel = guideById.get(id) || original;
          const differs = flatLabelDiffersFromOriginal(guideLabel, original, fields);
          return (
            <Accordion
              key={id}
              elevation={0}
              disableGutters
              square
              className="app-row app-row--interactive element-labels-dialog__element"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className="element-labels-dialog__element-summary"
              >
                <Box className="element-labels-dialog__element-heading">
                  <Typography className="element-labels-dialog__element-name">
                    {guideLabel.displayName}
                  </Typography>
                  {differs ? (
                    <Chip
                      label={translate("Differs from System")}
                      size="small"
                      className="element-labels-dialog__difference-chip"
                    />
                  ) : null}
                </Box>
              </AccordionSummary>
              <AccordionDetails className="element-labels-dialog__element-details">
                <Box className="element-labels-dialog__section-heading">
                  <Typography variant="subtitle2">{typeName}</Typography>
                  <AppButton
                    kind="ghost"
                    onClick={() =>
                      onChange(resetFlatLabel(labels, originalLabels, idField, id, fields))
                    }
                    disabled={!differs}
                  >{translateRich("Reset {value0}", {
  value0: typeName.toLocaleLowerCase()
})}</AppButton>
                </Box>
                <Box className="element-labels-dialog__field-grid">
                  {fields.map((field) => (
                    <LabelField
                      key={field}
                      label={
                        field === "displayName"
                          ? translate("Display name")
                          : field === "deactivatedDisplayName"
                            ? translate("Deactivated display name")
                            : translate("Description")
                      }
                      value={guideLabel[field]}
                      originalValue={original[field]}
                      required={field === "displayName"}
                      multiline={field === "description"}
                      onChange={(value) =>
                        onChange(updateFlatLabelField(labels, idField, id, field, value))
                      }
                      onReset={() =>
                        onChange(
                          resetFlatLabelField(
                            labels,
                            originalLabels,
                            idField,
                            id,
                            field
                          )
                        )
                      }
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
      {visibleLabels.length === 0 ? (
        <Typography className="element-labels-dialog__empty">{translateRich("No current {value0} labels match this view.", {
  value0: typeName.toLocaleLowerCase()
})}</Typography>
      ) : null}
    </Box>
  );
};

const ElementLabelsDialog = ({
  open,
  onClose,
  onSave,
  saveDisabled = false,
  elementLabels,
  originalElementLabels,
  environmentLabels = [],
  originalEnvironmentLabels = [],
  actionButtonLabels = [],
  originalActionButtonLabels = [],
  onChange,
  onEnvironmentLabelsChange,
  onActionButtonLabelsChange,
}) => {
  const locale = useLocale();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState("all");
  const [expandedElementId, setExpandedElementId] = useState("");
  const [resetAllConfirmOpen, setResetAllConfirmOpen] = useState(false);

  const guideById = useMemo(
    () => new Map(elementLabels.map((elementLabel) => [elementLabel.elementId, elementLabel])),
    [elementLabels, locale]
  );
  const stats = useMemo(
    () => getElementLabelStats(elementLabels, originalElementLabels),
    [elementLabels, originalElementLabels, locale]
  );
  const environmentStats = useMemo(
    () =>
      getFlatLabelStats(
        environmentLabels,
        originalEnvironmentLabels,
        "environmentId",
        ["displayName", "description"]
      ),
    [environmentLabels, originalEnvironmentLabels, locale]
  );
  const actionButtonFields = useMemo(
    () => ["displayName", "deactivatedDisplayName", "description"],
    [locale]
  );
  const actionButtonStats = useMemo(
    () =>
      getFlatLabelStats(
        actionButtonLabels,
        originalActionButtonLabels,
        "actionButtonId",
        actionButtonFields
      ),
    [actionButtonFields, actionButtonLabels, originalActionButtonLabels, locale]
  );

  const visibleOriginalLabels = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase();

    return originalElementLabels.filter((originalElementLabel) => {
      const guideElementLabel = guideById.get(originalElementLabel.elementId);
      const differs = elementLabelDiffersFromOriginal(guideElementLabel, originalElementLabel);

      if (filter === "different" && !differs) {
        return false;
      }

      if (!query) {
        return true;
      }

      const searchableNames = [
        originalElementLabel.displayName,
        guideElementLabel?.displayName,
        ...originalElementLabel.subElementLabels.map((label) => label.displayName),
        ...(guideElementLabel?.subElementLabels || []).map((label) => label.displayName),
      ];

      return searchableNames.some((name) =>
        String(name || "").toLocaleLowerCase().includes(query)
      );
    });
  }, [filter, guideById, originalElementLabels, searchText, locale]);

  const updateField = (elementId, field, value, subElementKey = null) => {
    onChange(updateElementLabelField(elementLabels, elementId, field, value, subElementKey));
  };

  const resetField = (elementId, field, subElementKey = null) => {
    onChange(
      resetElementLabelField(
        elementLabels,
        originalElementLabels,
        elementId,
        field,
        subElementKey
      )
    );
  };

  const handleResetAll = () => {
    onChange(resetAllCurrentElementLabels(elementLabels, originalElementLabels));
    onEnvironmentLabelsChange?.(
      resetAllFlatLabels(
        environmentLabels,
        originalEnvironmentLabels,
        "environmentId",
        ["displayName", "description"]
      )
    );
    onActionButtonLabelsChange?.(
      resetAllFlatLabels(
        actionButtonLabels,
        originalActionButtonLabels,
        "actionButtonId",
        actionButtonFields
      )
    );
    setResetAllConfirmOpen(false);
  };

  const handleSave = () => {
    onSave();
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="lg"
        PaperProps={{ className: "element-labels-dialog__paper" }}
      >
        <DialogTitle className="element-labels-dialog__title">{translate("Guide labels")}</DialogTitle>
        <DialogContent className="element-labels-dialog__content">
          <Typography variant="body2" className="element-labels-dialog__intro">{translate("These element, environment, and action-button labels belong to this guide and are shown to learners even when they skip the guide. Changes are committed with Save Guide.")}</Typography>

          <Box className="element-labels-dialog__summary" aria-live="polite">
            <Typography variant="body2">{translateRich("Current elements: {value0}", {value0: stats.currentElementCount})}</Typography>
            <Typography variant="body2">{translateRich("Current environments: {value0}", {value0: environmentStats.currentCount})}</Typography>
            <Typography variant="body2">{translateRich("Current action buttons: {value0}", {value0: actionButtonStats.currentCount})}</Typography>
            <Typography variant="body2">{translateRich("{value0} different from the current System", {
  value0: stats.differingElementCount + environmentStats.differingCount + actionButtonStats.differingCount
})}</Typography>
            {stats.orphanElementCount ||
            stats.orphanSubElementCount ||
            environmentStats.orphanCount ||
            actionButtonStats.orphanCount ? (
              <Typography variant="body2">{translateRich("Preserving removed labels — elements: {value0}; sub-elements: {value2}; environments: {value4}; action buttons: {value6}", {value0: stats.orphanElementCount, value2: stats.orphanSubElementCount, value4: environmentStats.orphanCount, value6: actionButtonStats.orphanCount})}</Typography>
            ) : null}
          </Box>

          <Box className="element-labels-dialog__toolbar">
            <AppTextField
              label={translate("Search labels")}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              className="element-labels-dialog__search"
            />
            <Box className="element-labels-dialog__filters" aria-label={translate("Element label filters")}>
              <AppButton
                kind="secondary"
                selected={filter === "all"}
                onClick={() => setFilter("all")}
              >{translate("All")}</AppButton>
              <AppButton
                kind="secondary"
                selected={filter === "different"}
                onClick={() => setFilter("different")}
              >{translate("Different")}</AppButton>
            </Box>
          </Box>

          <Box className="app-row-list element-labels-dialog__list">
            {visibleOriginalLabels.map((originalElementLabel) => {
              const guideElementLabel =
                guideById.get(originalElementLabel.elementId) || originalElementLabel;
              const differs = elementLabelDiffersFromOriginal(
                guideElementLabel,
                originalElementLabel
              );
              const guideSubElementsByKey = new Map(
                guideElementLabel.subElementLabels.map((label) => [label.key, label])
              );
              const isExpanded = expandedElementId === originalElementLabel.elementId;

              return (
                <Accordion
                  key={originalElementLabel.elementId}
                  elevation={0}
                  disableGutters
                  square
                  expanded={isExpanded}
                  onChange={(event, nextExpanded) => {
                    if (!event.defaultPrevented) {
                      setExpandedElementId(nextExpanded ? originalElementLabel.elementId : "");
                    }
                  }}
                  className="app-row app-row--interactive element-labels-dialog__element"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="element-labels-dialog__element-summary"
                  >
                    <Box className="element-labels-dialog__element-heading">
                      <Box className="element-labels-dialog__element-name-wrap">
                        <Typography className="element-labels-dialog__element-name">
                          {guideElementLabel.displayName}
                        </Typography>
                        {guideElementLabel.displayName !== originalElementLabel.displayName ? (
                          <Typography
                            variant="caption"
                            className="element-labels-dialog__system-name"
                          >{translateRich("System: {value0}", {
  value0: originalElementLabel.displayName
})}</Typography>
                        ) : null}
                      </Box>
                      <Box className="element-labels-dialog__element-meta">
                        <Typography variant="caption">{translateRich("Sub-elements: {value0}", {value0: originalElementLabel.subElementLabels.length})}</Typography>
                        {differs ? (
                          <Chip
                            label={translate("Differs from System")}
                            size="small"
                            className="element-labels-dialog__difference-chip"
                          />
                        ) : null}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  {isExpanded ? (
                  <AccordionDetails className="element-labels-dialog__element-details">
                    <Box className="element-labels-dialog__section-heading">
                      <Typography variant="subtitle2">{translate("Element")}</Typography>
                      <AppButton
                        kind="ghost"
                        onClick={() =>
                          onChange(
                            resetElementLabel(
                              elementLabels,
                              originalElementLabels,
                              originalElementLabel.elementId
                            )
                          )
                        }
                        disabled={!differs}
                      >{translate("Reset element")}</AppButton>
                    </Box>
                    <Box className="element-labels-dialog__field-grid">
                      <LabelField
                        label={translate("Display name")}
                        value={guideElementLabel.displayName}
                        originalValue={originalElementLabel.displayName}
                        required
                        onChange={(value) =>
                          updateField(originalElementLabel.elementId, "displayName", value)
                        }
                        onReset={() =>
                          resetField(originalElementLabel.elementId, "displayName")
                        }
                      />
                      <LabelField
                        label={translate("Description")}
                        value={guideElementLabel.description}
                        originalValue={originalElementLabel.description}
                        multiline
                        onChange={(value) =>
                          updateField(originalElementLabel.elementId, "description", value)
                        }
                        onReset={() =>
                          resetField(originalElementLabel.elementId, "description")
                        }
                      />
                    </Box>

                    {originalElementLabel.subElementLabels.length > 0 ? (
                      <Box className="element-labels-dialog__sub-elements">
                        <Typography variant="subtitle2">{translate("Sub-elements")}</Typography>
                        {originalElementLabel.subElementLabels.map((originalSubElementLabel) => {
                          const guideSubElementLabel =
                            guideSubElementsByKey.get(originalSubElementLabel.key) ||
                            originalSubElementLabel;

                          return (
                            <Box
                              key={originalSubElementLabel.key}
                              className="element-labels-dialog__sub-element"
                            >
                              <Typography
                                variant="caption"
                                className="element-labels-dialog__sub-element-key"
                              >
                                {originalSubElementLabel.key}
                              </Typography>
                              <Box className="element-labels-dialog__field-grid">
                                <LabelField
                                  label={translate("Display name")}
                                  value={guideSubElementLabel.displayName}
                                  originalValue={originalSubElementLabel.displayName}
                                  required
                                  onChange={(value) =>
                                    updateField(
                                      originalElementLabel.elementId,
                                      "displayName",
                                      value,
                                      originalSubElementLabel.key
                                    )
                                  }
                                  onReset={() =>
                                    resetField(
                                      originalElementLabel.elementId,
                                      "displayName",
                                      originalSubElementLabel.key
                                    )
                                  }
                                />
                                <LabelField
                                  label={translate("Description")}
                                  value={guideSubElementLabel.description}
                                  originalValue={originalSubElementLabel.description}
                                  multiline
                                  onChange={(value) =>
                                    updateField(
                                      originalElementLabel.elementId,
                                      "description",
                                      value,
                                      originalSubElementLabel.key
                                    )
                                  }
                                  onReset={() =>
                                    resetField(
                                      originalElementLabel.elementId,
                                      "description",
                                      originalSubElementLabel.key
                                    )
                                  }
                                />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : null}
                  </AccordionDetails>
                  ) : null}
                </Accordion>
              );
            })}
          </Box>

          {visibleOriginalLabels.length === 0 ? (
            <Typography className="element-labels-dialog__empty">{translate("No current element labels match this view.")}</Typography>
          ) : null}

          <FlatLabelsSection
            title={translate("Environments")}
            typeName={translate("Environment")}
            labels={environmentLabels}
            originalLabels={originalEnvironmentLabels}
            idField="environmentId"
            fields={["displayName", "description"]}
            searchText={searchText}
            filter={filter}
            onChange={onEnvironmentLabelsChange}
          />

          <FlatLabelsSection
            title={translate("Condition action buttons")}
            typeName={translate("Action button")}
            labels={actionButtonLabels}
            originalLabels={originalActionButtonLabels}
            idField="actionButtonId"
            fields={actionButtonFields}
            searchText={searchText}
            filter={filter}
            onChange={onActionButtonLabelsChange}
          />
        </DialogContent>
        <DialogActions className="element-labels-dialog__actions">
          <AppButton
            kind="danger"
            onClick={() => setResetAllConfirmOpen(true)}
            disabled={
              stats.differingElementCount +
                environmentStats.differingCount +
                actionButtonStats.differingCount ===
              0
            }
          >{translate("Reset all to System")}</AppButton>
          <Box className="element-labels-dialog__action-spacer" />
          <AppButton kind="ghost" onClick={onClose}>{translate("Done")}</AppButton>
          <AppButton kind="primary" onClick={handleSave} disabled={saveDisabled}>{translate("Save Guide")}</AppButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetAllConfirmOpen}
        onClose={() => setResetAllConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{translate("Reset all current labels?")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translate("This replaces every current element, sub-element, environment, and action-button label with the latest System values. Removed records remain preserved. Nothing is saved until you click Save Guide.")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton kind="ghost" onClick={() => setResetAllConfirmOpen(false)}>{translate("Cancel")}</AppButton>
          <AppButton kind="danger" onClick={handleResetAll}>{translate("Reset all")}</AppButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ElementLabelsDialog;
